import { useCallback, useContext, useLayoutEffect, useRef } from "react";
import { useToast } from "../../components/AlertToast/useToast";
import { CustomAlert } from "../../components/Alert/CustomAlert";
import { NotificationContext, useAppData, useConfigGuard } from "../../contexts/AppContext";
import {
  archiveAllNotifications,
  bulkMarkAsImportant,
  bulkMarkAsRead,
  changeFolder,
  countUnread,
  markAllNotificationsAsRead,
  updateNotification,
} from "../../_services/notifications.service";
import {
  IBulkAllResult,
  INotificationListData,
  IQueryListNotifications,
} from "../../_services/interface/notifications.interface";
import { caminhoInternoSeguro, viewDoItem } from "./notificationView";

/** Tempo do aviso com "Desfazer": curto demais e a pessoa não alcança o botão. */
const DURACAO_COM_DESFAZER = 8000;

// Sino, lista e leitor compartilham o contador; a última consulta iniciada vence.
const consultasContador = new Map<string, number>();
interface ConsumidorDeNotificacoes {
  atualizar: () => void;
  contar: () => Promise<void>;
  receberContagem: (unread: number) => void;
}
// O toast pode sobreviver ao leitor que o criou. A invalidação alcança os consumidores
// ainda montados do mesmo colaborador, inclusive quando o Desfazer vem desse toast.
const consumidores = new Map<string, Set<ConsumidorDeNotificacoes>>();

const ok = (status?: number | null) => status === 200 || status === 201;

const plural = (n: number, singular: string, pluralForm: string) => `${n} ${n === 1 ? singular : pluralForm}`;

/**
 * GEN-4101 — ações da central de notificações, as MESMAS no sino, na lista e no painel de leitura.
 *
 * Tudo que tira um aviso da vista (arquivar, marcar todas) mostra o aviso da casa (`CustomAlert` dentro
 * do toast, como o sino já fazia) com "Desfazer" — ação em massa sem volta foi uma das falhas apontadas
 * na revisão de Nielsen. Cada ação devolve `true`/`false` e quem chama atualiza a própria lista.
 */
export const useNotificationActions = (onChanged?: () => void) => {
  const toast = useToast();
  const { collaborator, guid_client } = useAppData();
  const { requestNavigation } = useConfigGuard();
  const notificationValues = useContext(NotificationContext);
  const guidCollaborator = collaborator?.guid_collaborator;
  const onChangedAtual = useRef(onChanged);
  onChangedAtual.current = onChanged;
  const contextoAtual = useRef(notificationValues);
  contextoAtual.current = notificationValues;

  const refreshCount = useCallback(async () => {
    if (!guidCollaborator) return;
    const grupo = consumidores.get(guidCollaborator);
    if (!grupo?.size) return;
    const consulta = (consultasContador.get(guidCollaborator) ?? 0) + 1;
    consultasContador.set(guidCollaborator, consulta);
    const result = await countUnread(guidCollaborator);
    if (consultasContador.get(guidCollaborator) !== consulta || consumidores.get(guidCollaborator) !== grupo) return;
    if (result.status === 200 && typeof result.response === "number") {
      // Quem iniciou pode ter fechado o leitor. Entrega ao consumidor atual do mesmo
      // colaborador; não há entrega a hooks desmontados nem a outro cliente.
      const atual = consumidores.get(guidCollaborator)?.values().next().value;
      atual?.receberContagem(result.response);
    }
  }, [guidCollaborator]);

  useLayoutEffect(() => {
    if (!guidCollaborator) return undefined;
    const consumidor = {
      atualizar: () => onChangedAtual.current?.(),
      contar: refreshCount,
      receberContagem: (unread: number) => contextoAtual.current.updateNotificationContext({ unread }),
    };
    const inscritos = consumidores.get(guidCollaborator) ?? new Set<ConsumidorDeNotificacoes>();
    inscritos.add(consumidor);
    consumidores.set(guidCollaborator, inscritos);
    return () => {
      inscritos.delete(consumidor);
      if (inscritos.size === 0) consumidores.delete(guidCollaborator);
    };
  }, [guidCollaborator, guid_client, refreshCount]);

  // A identidade do grupo sobrevive ao fechamento do leitor, mas não a uma troca A→B→A.
  const iniciarAcao = useCallback(() => {
    const grupo = guidCollaborator ? consumidores.get(guidCollaborator) : undefined;
    return () => !!guidCollaborator && !!grupo?.size && consumidores.get(guidCollaborator) === grupo;
  }, [guidCollaborator]);

  const avisar = useCallback(
    (
      titulo: string,
      descricao?: string,
      status: "success" | "error" = "success",
      desfazer?: () => void | Promise<void>
    ) =>
      toast({
        status,
        duration: desfazer ? DURACAO_COM_DESFAZER : 5000,
        isClosable: true,
        containerStyle: { maxW: "800px" },
        render: ({ onClose }) => (
          <CustomAlert
            closableFunc={onClose}
            descriptionHtml={`<div><b>${titulo}</b></div>${descricao ?? ""}`}
            icon={true}
            status={status}
            variant={"alert"}
            type={"three"}
            className={"alert-instance"}
            clicable={
              desfazer
                ? {
                    text: "Desfazer",
                    action: () => {
                      onClose();
                      void desfazer();
                    },
                  }
                : undefined
            }
          />
        ),
      }),
    [toast]
  );

  const falhou = useCallback(
    (acao: string) => avisar(`Não foi possível ${acao}`, "Tente de novo em instantes.", "error"),
    [avisar]
  );

  const depois = useCallback(async () => {
    if (!guidCollaborator) return;
    const inscritos = [...(consumidores.get(guidCollaborator) ?? [])];
    inscritos.forEach((consumidor) => consumidor.atualizar());
    // Uma consulta por mutação; a sequência compartilhada descarta respostas ultrapassadas.
    await inscritos[0]?.contar();
  }, [guidCollaborator]);

  /** Lida/não lida de UM aviso. */
  const marcarLida = useCallback(
    async (item: INotificationListData, lida: boolean) => {
      const atual = iniciarAcao();
      if (!atual()) return false;
      const result = await updateNotification(item.guid_notification, { readInSystem: lida });
      if (!atual()) return false;
      if (!ok(result.status) || result.response === false) {
        falhou(lida ? "marcar como lida" : "marcar como não lida");
        return false;
      }
      await depois();
      if (!atual()) return false;
      return true;
    },
    [depois, falhou, iniciarAcao]
  );

  /** Salvar/tirar dos salvos (o antigo "Importante"). */
  const alternarSalva = useCallback(
    async (item: INotificationListData) => {
      const atual = iniciarAcao();
      if (!atual()) return false;
      const result = await updateNotification(item.guid_notification, { isImportant: !item.isImportant });
      if (!atual()) return false;
      if (!ok(result.status) || result.response === false) {
        falhou(item.isImportant ? "tirar dos salvos" : "salvar");
        return false;
      }
      await depois();
      if (!atual()) return false;
      return true;
    },
    [depois, falhou, iniciarAcao]
  );

  /** Arquiva (ou volta para a Entrada) uma lista de avisos, com Desfazer. */
  const moverPasta = useCallback(
    async (guids: string[], arquivar: boolean, comDesfazer = true): Promise<boolean> => {
      if (!guid_client || guids.length === 0) return false;
      const atual = iniciarAcao();
      if (!atual()) return false;
      const result = await changeFolder(guid_client, { guids, folder: arquivar ? "ARCHIVED" : "INBOX" });
      if (!atual()) return false;
      if (!ok(result.status) || result.response === false) {
        falhou(arquivar ? "arquivar" : "desarquivar");
        return false;
      }
      await depois();
      if (!atual()) return false;
      if (comDesfazer) {
        avisar(
          arquivar
            ? `${plural(guids.length, "notificação arquivada", "notificações arquivadas")}`
            : `${plural(guids.length, "notificação voltou", "notificações voltaram")} para a Entrada`,
          arquivar ? "Você encontra tudo em Arquivadas." : undefined,
          "success",
          () => (atual() ? moverPasta(guids, !arquivar, false).then(() => undefined) : undefined)
        );
      }
      return true;
    },
    [guid_client, depois, falhou, avisar, iniciarAcao]
  );

  /** Lida/não lida em lote (seleção). */
  const marcarLidasEmLote = useCallback(
    async (guids: string[], lida: boolean, comDesfazer = true): Promise<boolean> => {
      if (!guidCollaborator || guids.length === 0) return false;
      const atual = iniciarAcao();
      if (!atual()) return false;
      const result = await bulkMarkAsRead(guidCollaborator, lida, guids);
      if (!atual()) return false;
      if (!ok(result.status) || result.response === false) {
        falhou(lida ? "marcar como lidas" : "marcar como não lidas");
        return false;
      }
      await depois();
      if (!atual()) return false;
      if (comDesfazer) {
        avisar(
          `${plural(guids.length, "notificação marcada", "notificações marcadas")} como ${lida ? "lida" : "não lida"}${
            guids.length === 1 ? "" : "s"
          }`,
          undefined,
          "success",
          () => (atual() ? marcarLidasEmLote(guids, !lida, false).then(() => undefined) : undefined)
        );
      }
      return true;
    },
    [guidCollaborator, depois, falhou, avisar, iniciarAcao]
  );

  /** Salvar em lote. */
  const salvarEmLote = useCallback(
    async (guids: string[], salvar: boolean): Promise<boolean> => {
      if (!guidCollaborator || guids.length === 0) return false;
      const atual = iniciarAcao();
      if (!atual()) return false;
      const result = await bulkMarkAsImportant(guidCollaborator, salvar, guids);
      if (!atual()) return false;
      if (!ok(result.status) || result.response === false) {
        falhou(salvar ? "salvar" : "tirar dos salvos");
        return false;
      }
      await depois();
      if (!atual()) return false;
      return true;
    },
    [guidCollaborator, depois, falhou, iniciarAcao]
  );

  /** "Marcar todas como lidas" no servidor, respeitando os filtros, com Desfazer. */
  const marcarTodasComoLidas = useCallback(
    async (filtros: Partial<IQueryListNotifications> = {}): Promise<boolean> => {
      if (!guidCollaborator) return false;
      const atual = iniciarAcao();
      if (!atual()) return false;
      try {
        const result = await markAllNotificationsAsRead(guidCollaborator, filtros);
        if (!atual()) return false;
        if (!ok(result.status) || !result.response) {
          falhou("marcar todas como lidas");
          return false;
        }
        const { count, guids, hasMore } = result.response as IBulkAllResult;
        await depois();
        if (!atual()) return false;
        if (count === 0) {
          avisar("Nada para marcar", "Todas já estavam lidas.");
          return true;
        }
        avisar(
          `${plural(count, "notificação marcada", "notificações marcadas")} como lida${count === 1 ? "" : "s"}`,
          hasMore ? "Ainda há mais não lidas; repita para continuar." : undefined,
          "success",
          () => (atual() ? marcarLidasEmLote(guids, false, false).then(() => undefined) : undefined)
        );
        return true;
      } catch {
        if (atual()) falhou("marcar todas como lidas");
        return false;
      }
    },
    [guidCollaborator, depois, falhou, avisar, marcarLidasEmLote, iniciarAcao]
  );

  /** "Arquivar todas" da Entrada (com os filtros), com Desfazer. */
  const arquivarTodas = useCallback(
    async (filtros: Partial<IQueryListNotifications> = {}): Promise<boolean> => {
      if (!guidCollaborator) return false;
      const atual = iniciarAcao();
      if (!atual()) return false;
      try {
        const result = await archiveAllNotifications(guidCollaborator, filtros);
        if (!atual()) return false;
        if (!ok(result.status) || !result.response) {
          falhou("arquivar todas");
          return false;
        }
        const { count, guids, hasMore } = result.response as IBulkAllResult;
        await depois();
        if (!atual()) return false;
        if (count === 0) {
          avisar("Nada para arquivar", "A Entrada já está vazia.");
          return true;
        }
        avisar(
          `${plural(count, "notificação arquivada", "notificações arquivadas")}`,
          hasMore ? "Ainda há mais na Entrada; repita para continuar." : "Você encontra tudo em Arquivadas.",
          "success",
          () => (atual() ? moverPasta(guids, false, false).then(() => undefined) : undefined)
        );
        return true;
      } catch {
        if (atual()) falhou("arquivar todas");
        return false;
      }
    },
    [guidCollaborator, depois, falhou, avisar, moverPasta, iniciarAcao]
  );

  /** Efeito de leitura/fechamento só depois da confirmação da navegação. */
  const depoisDeNavegar = useCallback(
    (item: INotificationListData, aoSair?: () => void) => {
      const atual = iniciarAcao();
      return () => {
        if (!atual()) return;
        aoSair?.();
        if (!item.readInSystem) {
          void updateNotification(item.guid_notification, { readInSystem: true }).then(() => {
            if (atual()) void depois();
          });
        }
      };
    },
    [iniciarAcao, depois]
  );

  /** Clique no aviso vai ao objeto; sem destino conhecido, abre o detalhe na Central. */
  const abrirDestino = useCallback(
    (item: INotificationListData, aoSair?: () => void) => {
      const view = viewDoItem(item);
      const link = caminhoInternoSeguro(view.link);
      const destino = link ?? `/notificacoes/${item.guid_notification}`;
      if (link && view.product === "activities") {
        window.open(destino, "_blank", "noopener,noreferrer");
        depoisDeNavegar(item)();
        return;
      }
      requestNavigation(destino, depoisDeNavegar(item, aoSair));
    },
    [depoisDeNavegar, requestNavigation]
  );

  /** Ação principal: rota interna protegida; arquivo externo em nova aba. */
  const executarAcao = useCallback(
    (item: INotificationListData, aoSair?: () => void) => {
      const acao = viewDoItem(item).action;
      if (!acao) return;
      const destino = acao.external ? acao.link : caminhoInternoSeguro(acao.link);
      if (!destino) return;
      if (acao.external || viewDoItem(item).product === "activities") {
        window.open(destino, "_blank", "noopener,noreferrer");
        depoisDeNavegar(item)();
        return;
      }
      requestNavigation(destino, depoisDeNavegar(item, aoSair));
    },
    [depoisDeNavegar, requestNavigation]
  );

  return {
    refreshCount,
    marcarLida,
    alternarSalva,
    moverPasta,
    marcarLidasEmLote,
    salvarEmLote,
    marcarTodasComoLidas,
    arquivarTodas,
    abrirDestino,
    executarAcao,
  };
};
